import { useEffect, useState } from "react";
import { ParentGetGrades, ParentGetGradesMonthlyProgress } from "services/Parent/ParentServices";
import ParentLoader from "./ParentLoader";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import ParentEmpty from "./ParentEmpty";
import ParentFmt from "./ParentFmt";
import { useAuth } from "context/AuthContext";

const ParentGradesTab = ({ studentId }) => {
    const { userInfo } = useAuth();
    const [grades, setGrades] = useState(null);
    const [monthly, setMonthly] = useState(null);

    useEffect(() => {

        Promise.all([

            ParentGetGrades(userInfo?.token, Number(studentId)),
            ParentGetGradesMonthlyProgress(userInfo?.token, Number(studentId)),

        ]).then(([g, m]) => { setGrades(g.grades); setMonthly(m.monthlyProgress); })

            .catch(() => { setGrades([]); setMonthly([]); });

    }, [studentId, userInfo]);



    if (!grades || !monthly) return <ParentLoader />;



    const avg = grades.length ? Math.round(grades.reduce((s, g) => s + g.result, 0) / grades.length) : 0;

    const best = grades.length ? Math.max(...grades.map(g => g.result)) : 0;



    const chartData = monthly.map(m => ({

        month: m.month,

        avg: parseFloat(parseFloat(m.avg_score).toFixed(1)),

        max: m.max_score,

        min: m.min_score,

    }));



    return (

        <div className="space-y-6">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                <div className="bg-blue-500/10 dark:bg-blue-500/20 border border-blue-500/30 rounded-2xl p-5 text-center">

                    <div className="text-4xl font-black text-blue-600 dark:text-blue-400">{grades.length}</div>

                    <div className="text-base font-bold text-blue-800 dark:text-blue-200 mt-2">إجمالي الاختبارات</div>

                </div>

                <div className="bg-purple-500/10 dark:bg-purple-500/20 border border-purple-500/30 rounded-2xl p-5 text-center">

                    <div className="text-4xl font-black text-purple-600 dark:text-purple-400">{avg}</div>

                    <div className="text-base font-bold text-purple-800 dark:text-purple-200 mt-2">متوسط الدرجات</div>

                </div>

                <div className="bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-5 text-center">

                    <div className="text-4xl font-black text-emerald-600 dark:text-emerald-400">{best}</div>

                    <div className="text-base font-bold text-emerald-800 dark:text-emerald-200 mt-2">أعلى درجة</div>

                </div>

            </div>



            {chartData.length > 0 && (

                <div className="bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-2xl p-6 shadow-sm">

                    <h3 className="text-lg font-bold text-watomsBlue dark:text-white mb-6">📈 تقدم الدرجات الشهري</h3>

                    <ResponsiveContainer width="100%" height={280}>

                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>

                            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />

                            <XAxis dataKey="month" stroke="currentColor" className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />

                            <YAxis stroke="currentColor" className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} domain={[0, 100]} />

                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />

                            <Legend wrapperStyle={{ paddingTop: '20px' }} />

                            <Line type="monotone" dataKey="avg" name="المتوسط" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', r: 5, strokeWidth: 0 }} activeDot={{ r: 8 }} />

                            <Line type="monotone" dataKey="max" name="الأعلى" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} />

                            <Line type="monotone" dataKey="min" name="الأدنى" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />

                        </LineChart>

                    </ResponsiveContainer>

                </div>

            )}



            {grades.length === 0 ? <ParentEmpty /> : (

                <div className="bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">

                    <div className="overflow-x-auto">

                        <table className="w-full text-base text-right">

                            <thead className="bg-gray-100/80 dark:bg-white/10 text-gray-900 dark:text-gray-100 font-bold border-b border-gray-300 dark:border-white/20">

                                <tr>

                                    <th className="px-6 py-4 max-w-[200px] truncate">الاختبار / الامتحان</th>

                                    <th className="px-6 py-4">المادة</th>

                                    <th className="px-6 py-4">المعلم</th>

                                    <th className="px-6 py-4">النوع</th>

                                    <th className="px-6 py-4">الدرجة</th>

                                    <th className="px-6 py-4">التاريخ</th>

                                </tr>

                            </thead>

                            <tbody className="divide-y divide-gray-200 dark:divide-white/5">

                                {grades.map(g => {

                                    const score = g.result ?? 0;

                                    const scoreColor = score >= 85 ? '#10b981' : score >= 65 ? '#f59e0b' : '#ef4444';

                                    const emp = g.teacher?.employee;

                                    const teacherName = emp ? `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name}`.trim() : '—';

                                    return (

                                        <tr key={g.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">

                                            <td className="px-6 py-4 text-gray-900 dark:text-gray-100 font-bold max-w-[200px] truncate" title={g.template?.name}>{g.template?.name || '—'}</td>

                                            <td className="px-6 py-4 text-gray-800 dark:text-gray-300 font-medium">{g.template?.subject?.name || '—'}</td>

                                            <td className="px-6 py-4 text-gray-800 dark:text-gray-300 font-medium">{teacherName}</td>

                                            <td className="px-6 py-4">

                                                <span className="bg-gray-200 dark:bg-white/10 text-gray-800 dark:text-gray-200 px-3 py-1 rounded-full text-xs font-bold shadow-sm">{g.template?.type || '—'}</span>

                                            </td>

                                            <td className="px-6 py-4">

                                                <span className="px-3 py-1 rounded-full text-sm font-black shadow-sm" style={{ backgroundColor: `${scoreColor}22`, color: scoreColor, border: `1px solid ${scoreColor}44` }}>

                                                    {score}

                                                </span>

                                            </td>

                                            <td className="px-6 py-4 text-gray-700 dark:text-gray-400 whitespace-nowrap font-medium">{ParentFmt(g.createdAt)}</td>

                                        </tr>

                                    );

                                })}

                            </tbody>

                        </table>

                    </div>

                </div>

            )}

        </div>

    );
}

export default ParentGradesTab;